import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan");
  const interval = searchParams.get("interval");
  const navigate = useNavigate();
  const { refresh: refreshAuth } = useAuth();
  const [state, setState] = useState("loading"); // loading | success | error
  const [error, setError] = useState("");
  const [nextUrl, setNextUrl] = useState("/admin");
  // StrictMode double-mount guard: only ever fire the API call once per token.
  // Without this, React 18 dev double-invokes the effect → 1st call consumes
  // the token + sets email_verified=True, 2nd call gets 400, user sees error.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return undefined;
    firedRef.current = true;
    // No `cancelled` flag here: StrictMode does mount → unmount → mount on the
    // same instance, and the in-flight API call from the first mount must be
    // allowed to setState on remount. React's "setState on unmounted component"
    // warning is benign for one-shot navigation flows like this.
    (async () => {
      try {
        const qp = new URLSearchParams();
        if (plan) qp.set("plan", plan);
        if (interval) qp.set("interval", interval);
        const { data } = await api.get(`/auth/verify-email/${token}${qp.toString() ? `?${qp}` : ""}`);
        setNextUrl(data.next || "/admin");
        setState("success");
        if (refreshAuth) {
          try { await refreshAuth(); } catch { /* non-fatal */ }
        }
        setTimeout(() => navigate(data.next || "/admin"), 1800);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
        setState("error");
      }
    })();
    return undefined;
  }, [token, plan, interval, navigate, refreshAuth]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-sky-600 flex items-center justify-center">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <span className="font-extrabold font-display tracking-tight">Production Pay Tracker</span>
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="rounded-2xl border-slate-200 w-full max-w-md">
          <CardContent className="p-8 text-center space-y-4" data-testid="verify-email-card">
            {state === "loading" && (
              <>
                <Loader2 className="h-10 w-10 mx-auto text-sky-600 animate-spin" />
                <h2 className="text-xl font-bold font-display">Verifying your email…</h2>
                <p className="text-sm text-slate-600">Hang tight, this only takes a moment.</p>
              </>
            )}
            {state === "success" && (
              <>
                <div className="h-14 w-14 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-7 w-7 text-green-700" />
                </div>
                <h2 className="text-2xl font-extrabold font-display tracking-tighter text-slate-900" data-testid="verify-success-title">
                  Email verified!
                </h2>
                <p className="text-sm text-slate-600">
                  Welcome aboard. {nextUrl.includes("/admin/billing") ? "Redirecting you to checkout…" : "Loading your dashboard…"}
                </p>
                <Button asChild className="w-full bg-sky-600 hover:bg-sky-700" data-testid="verify-continue-btn">
                  <Link to={nextUrl}>Continue →</Link>
                </Button>
              </>
            )}
            {state === "error" && (
              <>
                <div className="h-14 w-14 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="h-7 w-7 text-red-700" />
                </div>
                <h2 className="text-2xl font-extrabold font-display tracking-tighter text-slate-900" data-testid="verify-error-title">
                  Verification failed
                </h2>
                <p className="text-sm text-slate-600">{error || "This link is invalid or has expired."}</p>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/signup">Sign up again</Link>
                </Button>
                <Button asChild variant="ghost" className="w-full">
                  <Link to="/login">Or sign in</Link>
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
